import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CARD_NAME_MAX_LINES, TAPPED_ROTATION_DEG } from '../../../shared/playtest';
import { FRAME_BORDER, FRAME_RADIUS } from './playtestStyles';

const SPACING = { gap: 4, padding: 4 };
/** Upright tile footprint in px. A tapped tile turns, so its slot swaps width and height. */
export const TILE_WIDTH = 120;
export const CARD_HEIGHT = 240;
/** Attached cards are drawn smaller under their host. */
export const COMPACT_TILE_WIDTH = 80;
export const COMPACT_CARD_HEIGHT = 160;
const IMAGE_HEIGHT = 168;
const COMPACT_IMAGE_HEIGHT = 112;
const IMAGE_RADIUS = 3;
/** Failed loads of one image URL that are retried before the tile gives up on it. */
const IMAGE_RETRY_LIMIT = 2;
/** Wait before each retry. */
const IMAGE_RETRY_DELAY_MS = 500;
const NAME_WEIGHT = 600;
const TYPE_LINE_FONT_SIZE = '0.8em';
const MUTED_OPACITY = 0.75;
const SELECTED_OUTLINE = '2px solid #1890ff';

/** Footprint for an upright tile, by size. */
export const tileDimensions = (compact) =>
  compact
    ? { width: COMPACT_TILE_WIDTH, height: COMPACT_CARD_HEIGHT }
    : { width: TILE_WIDTH, height: CARD_HEIGHT };

const styles = {
  tile: {
    boxSizing: 'border-box',
    flex: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.gap,
    padding: SPACING.padding,
    background: 'transparent',
    border: FRAME_BORDER,
    borderRadius: FRAME_RADIUS,
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  selected: { outline: SELECTED_OUTLINE },
  image: { width: '100%', height: IMAGE_HEIGHT, objectFit: 'cover', borderRadius: IMAGE_RADIUS },
  compactImage: { width: '100%', height: COMPACT_IMAGE_HEIGHT, objectFit: 'cover', borderRadius: IMAGE_RADIUS },
  name: {
    fontWeight: NAME_WEIGHT,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: CARD_NAME_MAX_LINES,
  },
  typeLine: { fontSize: TYPE_LINE_FONT_SIZE, opacity: MUTED_OPACITY },
};

/**
 * One card face in a grid. The whole tile is a button; `onClick` may be omitted
 * for static tiles. `tapped` (battlefield only) turns the tile a quarter turn.
 * `dragProps` makes the tile a drag source.
 */
const CardTile = ({
  testId,
  name,
  typeLine,
  imageSmall,
  selected,
  tapped,
  compact,
  ariaLabel,
  dragProps,
  onClick,
  onContextMenu,
  children,
}) => {
  // A failed image is retried a bounded number of times, then dropped for that URL, so a different URL gets a fresh try.
  const [load, setLoad] = useState(null);
  const current = load !== null && load.src === imageSmall ? load : null;
  const failures = current?.failures ?? 0;
  const hidden = current !== null && (current.waiting || failures > IMAGE_RETRY_LIMIT);
  const showImage = Boolean(imageSmall) && !hidden;

  // Ends a retry wait by showing the image again; cleared if the load state changes first.
  useEffect(() => {
    if (load === null || !load.waiting) return undefined;
    const id = setTimeout(() => setLoad({ ...load, waiting: false }), IMAGE_RETRY_DELAY_MS);
    return () => clearTimeout(id);
  }, [load]);

  const handleImageError = () => {
    const failed = failures + 1;
    setLoad({ src: imageSmall, failures: failed, waiting: failed <= IMAGE_RETRY_LIMIT });
  };
  const dimensions = tileDimensions(compact);
  const tileStyle = {
    ...styles.tile,
    width: dimensions.width,
    minHeight: dimensions.height,
    ...(selected ? styles.selected : null),
    ...(tapped ? { transform: `rotate(${TAPPED_ROTATION_DEG}deg)` } : null),
  };
  return (
    <button
      type="button"
      data-testid={testId}
      data-selected={selected ? 'true' : 'false'}
      data-tapped={tapped === null ? undefined : String(tapped)}
      aria-label={ariaLabel ?? undefined}
      style={tileStyle}
      onClick={onClick}
      onContextMenu={onContextMenu}
      {...dragProps}
    >
      {showImage ? (
        <img
          src={imageSmall}
          alt={name}
          style={compact ? styles.compactImage : styles.image}
          onError={handleImageError}
        />
      ) : null}
      <span style={styles.name}>{name}</span>
      {typeLine ? <span style={styles.typeLine}>{typeLine}</span> : null}
      {children}
    </button>
  );
};

CardTile.propTypes = {
  testId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  typeLine: PropTypes.string,
  imageSmall: PropTypes.string,
  selected: PropTypes.bool,
  tapped: PropTypes.bool,
  compact: PropTypes.bool,
  ariaLabel: PropTypes.string,
  dragProps: PropTypes.object,
  onClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  children: PropTypes.node,
};

CardTile.defaultProps = {
  typeLine: null,
  imageSmall: null,
  selected: false,
  tapped: null,
  compact: false,
  ariaLabel: null,
  dragProps: undefined,
  onClick: undefined,
  onContextMenu: undefined,
  children: null,
};

export default CardTile;
